import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Sparkles, CheckCircle2, ArrowUpRight, Loader2, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function Billing() {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [coupon, setCoupon] = useState("");
  const [checkingOut, setCheckingOut] = useState(null);
  const [polling, setPolling] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const sessionId = params.get("session_id");

  const loadPlans = async () => {
    try {
      const { data } = await api.get("/billing/plans");
      setPlans(data.rows);
    } catch (e) {}
  };
  useEffect(() => { loadPlans(); }, []);

  // Poll Stripe checkout status when returning from Stripe
  useEffect(() => {
    if (!sessionId || polling) return;
    setPolling(true);
    let attempts = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPaymentResult({ ok: true, ...data });
          toast.success(`Payment successful · welcome to ${data.plan_slug}`);
          if (refresh) await refresh();
          setParams({}, { replace: true });
          setPolling(false);
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setPaymentResult({ ok: false, ...data });
          setPolling(false);
          return;
        }
        attempts += 1;
        if (attempts < 6) setTimeout(tick, 2000);
        else { setPaymentResult({ pending: true, ...data }); setPolling(false); }
      } catch (e) {
        setPolling(false);
      }
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const startCheckout = async (slug) => {
    setCheckingOut(slug);
    try {
      const { data } = await api.post("/billing/checkout/start", {
        plan_slug: slug,
        coupon_code: coupon || undefined,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setCheckingOut(null);
    }
  };

  const myPlan = user?.plan || "professional_free";
  const filteredPlans = plans.filter((p) => {
    if (!user) return true;
    if (user.role === "professional") return p.role_target === "professional";
    if (user.role === "organization") return p.role_target === "organization";
    if (user.role === "institution") return p.role_target === "institution";
    return true;
  });

  return (
    <div className="min-h-screen bg-ec-bg" data-testid="billing-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10 space-y-8">
        <div>
          <div className="eyebrow">Billing & Subscription</div>
          <h1 className="font-display text-4xl font-black tracking-tight mt-2">Plans & checkout</h1>
        </div>

        {/* Payment result banner */}
        {polling && (
          <Card className="rounded-sm border-amber-500/40 bg-amber-500/5" data-testid="payment-polling">
            <CardContent className="p-5 flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
              <div>
                <div className="font-display font-bold">Confirming your payment…</div>
                <div className="text-sm text-ec-ink-soft">We're checking with Stripe — this only takes a second.</div>
              </div>
            </CardContent>
          </Card>
        )}
        {paymentResult?.ok && (
          <Card className="rounded-sm border-ec-emerald/40 bg-ec-emerald/5" data-testid="payment-success">
            <CardContent className="p-5 flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-ec-emerald" />
              <div>
                <div className="font-display font-bold">Payment confirmed</div>
                <div className="text-sm text-ec-ink-soft">Welcome to <span className="font-mono-plex font-bold">{paymentResult.plan_slug}</span>. Receipt sent to your email.</div>
              </div>
            </CardContent>
          </Card>
        )}
        {paymentResult && paymentResult.ok === false && (
          <Card className="rounded-sm border-ec-error/40 bg-ec-error/5" data-testid="payment-failed">
            <CardContent className="p-5 flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-ec-error" />
              <div>
                <div className="font-display font-bold">Payment did not complete</div>
                <div className="text-sm text-ec-ink-soft">Status: {paymentResult.payment_status}. Try again or pick a different plan.</div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="rounded-sm border-ec-line bg-ec-soft" data-testid="current-plan-card">
          <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="eyebrow">Current plan</div>
              <div className="font-display text-2xl font-black mt-1 flex items-center gap-2" data-testid="current-plan-name">
                {myPlan}
                <Badge className="rounded-sm bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30 gap-1"><ShieldCheck className="w-3 h-3" />{user?.plan_status || "active"}</Badge>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="COUPON CODE" className="h-10 border-ec-line px-3 font-mono-plex text-sm rounded-sm w-44" data-testid="billing-coupon-input" />
              <span className="text-xs text-ec-ink-soft">Applied at checkout</span>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="billing-plan-grid">
          {filteredPlans.map((p) => {
            const current = myPlan === p.slug;
            const free = p.monthly_price === 0;
            return (
              <Card key={p.slug} className={`rounded-sm ${current ? "border-ec-ink ring-1 ring-ec-ink" : "border-ec-line"} flex flex-col`} data-testid={`billing-plan-${p.slug}`}>
                <CardHeader className="border-b border-ec-line">
                  <CardTitle className="font-display flex items-center gap-2"><Sparkles className="w-4 h-4 text-ec-emerald" /> {p.name}</CardTitle>
                </CardHeader>
                <CardContent className="p-6 flex-1 flex flex-col">
                  <div className="font-display text-4xl font-black tracking-tighter">{free ? "Free" : `$${p.monthly_price.toFixed(2)}`}<span className="text-base font-medium text-ec-ink-soft">{!free && "/mo"}</span></div>
                  {p.description && <div className="text-sm text-ec-ink-soft mt-2">{p.description}</div>}
                  <ul className="mt-4 space-y-1.5 text-sm flex-1">
                    {(p.features || []).map((f) => <li key={f} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-ec-emerald mt-0.5 flex-shrink-0" /> {f}</li>)}
                  </ul>
                  <Button
                    onClick={() => !free && startCheckout(p.slug)}
                    disabled={current || free || checkingOut === p.slug}
                    className={`rounded-sm mt-6 w-full gap-2 ${current ? "bg-ec-mute text-ec-ink-soft" : "bg-ec-ink hover:bg-zinc-800"}`}
                    data-testid={`billing-select-${p.slug}`}
                  >
                    {current ? "Current plan" : free ? "Included" : checkingOut === p.slug ? <><Loader2 className="w-4 h-4 animate-spin" />Redirecting…</> : <>Subscribe <ArrowUpRight className="w-4 h-4" /></>}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <p className="text-xs eyebrow text-ec-ink-soft">
          Payments processed via Stripe (test mode). Real-time refunds and subscription extends are available through the Super Admin portal.
        </p>
      </div>
      <Footer />
    </div>
  );
}
